import React from 'react';
import { useTranslation } from 'react-i18next';
import ContainedSideSheet from './ui/ContainedSideSheet';
import BottomSheet from './student/BottomSheet';
import useIsCoachMobile from '../hooks/useIsCoachMobile';
import { isChangelogEntryUnseen } from '../hooks/useChangelog';

// Font Awesome « gift » (solid)
export const GiftIcon = ({ className, style }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 512 512"
    className={className}
    style={style}
    fill="currentColor"
  >
    <path d="M190.5 68.8L225.3 128H224 152c-22.1 0-40-17.9-40-40s17.9-40 40-40h2.2c14.9 0 28.8 7.9 36.3 20.8zM64 88c0 14.4 3.5 28 9.6 40H32c-17.7 0-32 14.3-32 32v64c0 17.7 14.3 32 32 32H480c17.7 0 32-14.3 32-32V160c0-17.7-14.3-32-32-32H438.4c6.1-12 9.6-25.6 9.6-40c0-48.6-39.4-88-88-88h-2.2c-31.9 0-61.5 16.9-77.7 44.4L256 85.5l-24.1-41C215.7 16.9 186.1 0 154.2 0H152C103.4 0 64 39.4 64 88zm336 0c0 22.1-17.9 40-40 40H288h-1.3l34.8-59.2C329.1 55.9 342.9 48 357.8 48H360c22.1 0 40 17.9 40 40zM32 288V464c0 26.5 21.5 48 48 48H224V288H32zM288 512H432c26.5 0 48-21.5 48-48V288H288V512z" />
  </svg>
);

const TAG_STYLES = {
  new: { backgroundColor: 'rgba(212, 132, 90, 0.15)', color: '#d4845a' },
  improved: { backgroundColor: 'rgba(96, 165, 250, 0.15)', color: '#93c5fd' },
  fixed: { backgroundColor: 'rgba(74, 222, 128, 0.12)', color: '#86efac' },
};

// 'AAAA-MM-JJ' lu en heure locale (new Date('AAAA-MM-JJ') serait en UTC et
// pourrait afficher la veille).
const formatEntryDate = (isoDate, lang) => {
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(lang, { day: 'numeric', month: 'short', year: 'numeric' });
};

/**
 * Points d'une nouveauté (volet et pop-up « Nouveautés ») : une puce cuivre
 * par point, son intitulé en clair. Un point = 'texte' ou ['Intitulé', 'texte'].
 */
export const ChangelogPoints = ({ points, lang, className = '' }) => (points?.length > 0 ? (
  <ul className={`space-y-1.5 ${className}`}>
    {points.map((point, i) => {
      const [label, text] = Array.isArray(point) ? point : [null, point];
      return (
        <li key={i} className="flex gap-2.5 text-[13px] font-light text-white/70 leading-relaxed">
          <span aria-hidden="true" className="mt-[8px] h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: 'var(--kaiylo-primary-hex)' }} />
          <span>
            {label && <span className="font-normal text-white">{label}{lang === 'fr' ? ' : ' : ': '}</span>}
            {text}
          </span>
        </li>
      );
    })}
  </ul>
) : null);

const ChangelogSidebar = ({ isOpen, onClose, entries = [], lastSeenDate = null, onNavigate }) => {
  const { t, i18n } = useTranslation('common');
  const lang = i18n.language?.startsWith('en') ? 'en' : 'fr';
  const pick = (value) => value?.[lang] ?? value?.fr ?? '';
  const unseenCount = entries.filter((entry) => isChangelogEntryUnseen(entry, lastSeenDate)).length;
  // Coach phone: a bottom sheet instead of the side panel.
  const isPhone = useIsCoachMobile();

  if (isPhone) {
    return (
      <BottomSheet isOpen={isOpen} onClose={onClose} labelledBy="changelog-sheet-title" testId="changelog-sheet" zClassName="z-[10000]" expandable>
        <div className="flex max-h-[85dvh] flex-col text-white [[data-sheet-resized]_&]:max-h-none">
          <div className="flex items-center gap-2 px-5 pt-1">
            <h2 id="changelog-sheet-title" className="text-[17px] font-bold text-white">{t('changelog.title')}</h2>
            {unseenCount > 0 && (
              <span className="flex h-[20px] min-w-[20px] items-center justify-center rounded-full bg-kaiylo-accent px-[6px] text-[11px] font-semibold text-white">{unseenCount}</span>
            )}
          </div>
          {renderBody(true)}
        </div>
      </BottomSheet>
    );
  }

  return (
    <ContainedSideSheet
      open={isOpen}
      onClose={onClose}
      title={t('changelog.title')}
      widthClass="max-w-lg"
      zIndex={9999}
      modalId="changelog-sidebar"
      badgeCount={unseenCount}
    >
      {renderBody(false)}
    </ContainedSideSheet>
  );

  function renderBody(phone) {
    const px = phone ? 'px-5' : 'px-6';
    return (
      <div className={`${phone ? 'min-h-0 flex-1' : 'h-full'} flex flex-col text-white`}>
        <p
          className={`${px} pt-1 pb-3 text-[14px] text-white/50 font-extralight border-b border-white/10`}
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {t('changelog.subtitle')}
        </p>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain notification-sidebar-scrollbar">
          {entries.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <GiftIcon style={{ width: '32px', height: '32px', color: 'rgba(255, 255, 255, 0.5)' }} />
              <h3 className="text-lg font-light mt-3" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                {t('changelog.empty')}
              </h3>
            </div>
          ) : (
            entries.map((entry) => {
              const isUnseen = isChangelogEntryUnseen(entry, lastSeenDate);
              return (
                <article
                  key={entry.id}
                  data-testid="changelog-entry"
                  data-unseen={isUnseen ? 'true' : 'false'}
                  className={`py-4 ${px} border-b border-white/5 ${isUnseen ? 'bg-[#2a1f1a]/60' : ''}`}
                  style={isUnseen ? { borderLeft: '3px solid var(--kaiylo-primary-hex)' } : {}}
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <span
                      className="text-[11px] font-medium px-2 py-0.5 rounded-full"
                      style={TAG_STYLES[entry.type] ?? TAG_STYLES.new}
                    >
                      {t(`changelog.types.${entry.type}`, { defaultValue: entry.type })}
                    </span>
                    <span className="text-[11px] text-gray-500 font-medium">{formatEntryDate(entry.date, lang)}</span>
                  </div>

                  <h3 className="text-[15px] font-normal text-white leading-snug">{pick(entry.title)}</h3>
                  {entry.body && <p className="text-[13px] font-light text-white/70 leading-relaxed mt-1">{pick(entry.body)}</p>}

                  <ChangelogPoints points={pick(entry.points)} lang={lang} className="mt-2" />

                  {entry.image && (
                    <img
                      src={entry.image}
                      alt=""
                      loading="lazy"
                      className="mt-3 w-full rounded-lg border border-white/10"
                    />
                  )}

                  {entry.cta?.path && (
                    <button
                      type="button"
                      onClick={() => onNavigate?.(entry.cta.path)}
                      className="mt-3 text-[12px] font-normal py-1 px-2.5 rounded-md bg-white/5 border transition-colors hover:bg-white/10"
                      style={{ color: 'var(--kaiylo-primary-hex)', borderColor: 'var(--kaiylo-primary-hex)' }}
                    >
                      {pick(entry.cta.label)}
                    </button>
                  )}
                </article>
              );
            })
          )}
        </div>
      </div>
    );
  }
};

export default ChangelogSidebar;
